import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import Avatar from 'components/common/Avatar';
import paths from 'routes/paths';
import { useProject } from 'contexts/ProjectContext';
import ApiService from 'helpers/apiService';
import { getCurrentProfileId } from 'helpers/access';

// Кеш данных текущего оператора в localStorage — чтобы имя сразу отображалось
// при навигации без повторного fetch. TTL = текущая сессия (logout чистит).
const OPERATOR_CACHE_KEY = 'currentOperator';

function readCachedOperator(profileId) {
  if (!profileId) return null;
  try {
    const raw = localStorage.getItem(OPERATOR_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    return cached && cached.id === profileId ? cached : null;
  } catch {
    return null;
  }
}

const ProfileDropdown = () => {
  const { projects, currentProjectId, setCurrentProjectId, loading } = useProject();
  const profileId = getCurrentProfileId();
  const [operator, setOperator] = useState(() => readCachedOperator(profileId));

  // Раньше было hardcoded "Nikitin Vladimir" — Avatar генерил инициалы NV
  // и непонятно было, под кем работает админка. Теперь имя оператора —
  // из /profile/v1/list (фильтр по id из JWT claim) с localStorage-кешем.
  // deps: только profileId — operator в useEffect не нужен (мы проверяем
  // cancelled-флаг и обновляем state только если профиль ещё не подгружен;
  // если бы добавили operator в deps — был бы лишний perma-fetch loop).
  useEffect(() => {
    if (!profileId) {
      setOperator(null);
      return undefined;
    }
    let cancelled = false;
    ApiService.getOperators(uuidv4())
      .then(resp => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const me = list.find(p => p.id === profileId);
        if (me) {
          const compact = {
            id: me.id,
            name: me.name || '',
            email: me.email || '',
            phone: me.phone || ''
          };
          setOperator(compact);
          try {
            localStorage.setItem(OPERATOR_CACHE_KEY, JSON.stringify(compact));
          } catch {
            /* ignore quota */
          }
        }
      })
      .catch(() => {
        /* нет доступа к /profile/v1/list (нет profileList access) —
           отображаем fallback «Оператор» вместо имени */
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentProjectId');
    localStorage.removeItem(OPERATOR_CACHE_KEY);
  };

  const displayName = operator?.name || 'Оператор';
  const displayContact = operator?.email || operator?.phone || '';

  return (
    <Dropdown navbar={true} as="li">
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className="pe-0 ps-2 nav-link d-flex align-items-center gap-2"
      >
        <Avatar size="xl" name={displayName} />
        <span
          className="d-none d-md-inline-block text-700 fw-semibold"
          style={{ fontSize: '0.85rem' }}
        >
          {displayName}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end">
        <div className="bg-white rounded-2 py-2 dark__bg-1000">
          <div className="px-3 py-2 border-bottom border-200">
            <div className="fw-semibold text-700">{displayName}</div>
            {displayContact && (
              <div className="text-muted small">{displayContact}</div>
            )}
          </div>
          <div className="px-3 py-2 border-bottom border-200">
            <span className="text-700 fw-semibold">Проект</span>
          </div>
          {!loading && projects.length > 0 && (
            <>
              {projects.map(project => (
                <Dropdown.Item
                  key={project.id}
                  onClick={() => setCurrentProjectId(project.id)}
                  active={currentProjectId === project.id}
                  className="d-flex align-items-center"
                >
                  <span className="me-2">{currentProjectId === project.id && '✓'}</span>
                  {project.name || project.code || project.id}
                </Dropdown.Item>
              ))}
            </>
          )}
          {!loading && projects.length === 0 && (
            <Dropdown.Item as={Link} to={paths.projects} className="text-muted">
              Нет доступных проектов
            </Dropdown.Item>
          )}
          <Dropdown.Divider />
          <Dropdown.Item as={Link} to={paths.cardLogout} onClick={handleLogout}>
            Выход
          </Dropdown.Item>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ProfileDropdown;
